import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import {
  WebsiteDigestIssueStatusSchema,
  FLOWSTATE_DIGEST_CHANNEL_SLUG,
} from "@flowstate/types";
import type { Json } from "@flowstate/types";

const jwtSchema = z
  .string()
  .min(1)
  .regex(/^[\w-]+\.[\w-]+\.[\w-]+$/, "Invalid JWT format");

export interface WebsiteDigestIssueRow {
  id: string;
  channel_id: string;
  slug: string;
  title: string;
  week_start: string;
  week_end: string;
  status: string;
  markdown_body: string | null;
  sources_json: Json | null;
  seo_title: string | null;
  seo_description: string | null;
  published_at: string | null;
  approved_at: string | null;
  updated_at: string;
}

async function createAuthedSupabaseClient(accessToken: string) {
  const { createClient } = await import("@supabase/supabase-js");
  const url = process.env.SUPABASE_URL ?? process.env.VITE_SUPABASE_URL;
  const anonKey = process.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) {
    return { client: null, user: null, error: "Missing server env vars" };
  }

  const client = createClient(url, anonKey, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: { headers: { Authorization: `Bearer ${accessToken}` } },
  });

  const {
    data: { user },
    error: authError,
  } = await client.auth.getUser();
  if (authError || !user) {
    return { client: null, user: null, error: "Unauthorized" };
  }

  return { client, user, error: null };
}

export const listWebsiteDigestIssues = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
      channelSlug: z.string().default(FLOWSTATE_DIGEST_CHANNEL_SLUG),
      status: WebsiteDigestIssueStatusSchema.optional(),
    }),
  )
  .handler(async ({ data }) => {
    const auth = await createAuthedSupabaseClient(data.accessToken);
    if (!auth.client || !auth.user) return { data: null, error: auth.error };

    const { data: channel } = await auth.client
      .from("website_digest_channels")
      .select("id")
      .eq("slug", data.channelSlug)
      .maybeSingle();

    if (!channel) return { data: null, error: "Channel not found" };

    let query = auth.client
      .from("website_digest_issues")
      .select(
        "id, channel_id, slug, title, week_start, week_end, status, markdown_body, sources_json, seo_title, seo_description, published_at, approved_at, updated_at",
      )
      .eq("channel_id", channel.id)
      .order("week_end", { ascending: false })
      .limit(50);

    if (data.status) {
      query = query.eq("status", data.status);
    }

    const { data: issues, error } = await query;
    if (error) return { data: null, error: error.message };

    return { data: { issues: (issues ?? []) as WebsiteDigestIssueRow[] }, error: null };
  });

export const updateWebsiteDigestIssue = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
      issueId: z.string().uuid(),
      title: z.string().min(1).optional(),
      markdownBody: z.string().optional(),
      status: WebsiteDigestIssueStatusSchema.optional(),
      rejectedReason: z.string().optional(),
    }),
  )
  .handler(async ({ data }) => {
    const auth = await createAuthedSupabaseClient(data.accessToken);
    if (!auth.client || !auth.user) return { data: null, error: auth.error };

    const patch: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (data.title !== undefined) patch.title = data.title;
    if (data.markdownBody !== undefined) patch.markdown_body = data.markdownBody;
    if (data.rejectedReason !== undefined) patch.rejected_reason = data.rejectedReason;

    if (data.status === "published") {
      patch.status = "published";
      patch.published_at = new Date().toISOString();
      patch.approved_by = auth.user.id;
      patch.approved_at = new Date().toISOString();
    } else if (data.status) {
      patch.status = data.status;
    }

    const { data: issue, error } = await auth.client
      .from("website_digest_issues")
      .update(patch)
      .eq("id", data.issueId)
      .select("id, slug, title, status, published_at")
      .single();

    if (error) return { data: null, error: error.message };

    if (data.status === "published" && issue) {
      const { createAdminClient } = await import("./_admin-utils.server");
      const { client: adminClient } = await createAdminClient();
      const { publishMyceliumEvent } = await import("./_mycelium.server");
      await publishMyceliumEvent(adminClient ?? auth.client, {
        domain: "documents",
        type: "website_digest_published",
        userId: auth.user.id,
        payload: {
          issueId: issue.id,
          slug: issue.slug,
          publishedAt: issue.published_at ?? undefined,
          approvedBy: auth.user.id,
          source: "flowstate-start-admin",
        },
      });
    }

    return { data: { issue }, error: null };
  });
